import { useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import { toast } from 'react-toastify'
import useServerAuth from 'src/customHooks/useServerAuth'
import {
  getDataMapFromCommonResponse,
  getImageLink,
  isCommonResponseSuccessfulV2,
} from 'src/mentor/helpers/utilityFunctions'
import { getSupportClinicBrandBySlug } from 'src/supportPortal/services/clinicPractitionerAffiliationService'
import {
  assignPractitionerToBrand,
  assignPractitionerToLocation,
  unassignPractitionerFromBrand,
  unassignPractitionerFromLocation,
} from 'src/supportPortal/services/clinicWorkforceService'

const ROLES = [
  { key: 'screener', label: 'Screeners', serviceKey: 'ROLE_SCREENER' },
  { key: 'therapist', label: 'Therapists', serviceKey: 'ROLE_THERAPIST' },
  { key: 'doctor', label: 'Doctors', serviceKey: 'ROLE_DOCTOR' },
]

function copyToClipboard(value, label) {
  if (!value) return
  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
    navigator.clipboard
      .writeText(value)
      .then(() => toast.success(`${label} copied`))
      .catch(() => toast.error(`Could not copy ${label}`))
  } else {
    toast.error('Clipboard not available')
  }
}

function mentorName(mentor) {
  return (
    mentor?.name ||
    [mentor?.firstName, mentor?.lastName].filter(Boolean).join(' ').trim() ||
    mentor?.title ||
    mentor?.email ||
    mentor?.slug ||
    mentor?.id ||
    'Unknown'
  )
}

function contactMatches(contact, practitionerId, userId) {
  if (!contact) return false
  return (
    (practitionerId && contact.uid === practitionerId) ||
    (userId && contact.userId === userId)
  )
}

function workforceListForRole(workforce, roleKey) {
  if (!workforce) return []
  if (roleKey === 'doctor') return workforce.doctors || []
  if (roleKey === 'therapist') return workforce.therapists || []
  if (roleKey === 'screener') return workforce.screeners || []
  return []
}

/**
 * "People" management for a clinic brand or location. Lists current members grouped by role with a
 * remove action, and an add picker. Brand candidates are any practitioner approved for the role;
 * location candidates are restricted to the brand's members for that role (a practitioner must be
 * on the brand before a location). Editing is gated to Clinic Success Admin / Manager (the backend
 * also scopes Managers to their clinics); others see read-only.
 */
export default function ClinicPeopleManagerDrawer({
  open,
  onClose,
  scope,
  brandId,
  brandSlug,
  brandTitle,
  location,
  mentors = [],
  onChanged,
}) {
  const { allRolesInBoolean } = useServerAuth()
  const canEdit = Boolean(
    allRolesInBoolean?.isClinicSuccessAdmin ||
      allRolesInBoolean?.isClinicSuccessManager ||
      allRolesInBoolean?.isAdmin,
  )
  const isLocation = scope === 'location'

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [brandWorkforce, setBrandWorkforce] = useState(null)
  // Brand id resolved from the fetched brand, as a fallback when the caller didn't pass brandId.
  const [resolvedBrandId, setResolvedBrandId] = useState('')
  // Per-role member lists ({ uid, userId, title }) for the current entity (brand or location).
  const [membersByRole, setMembersByRole] = useState({ screener: [], therapist: [], doctor: [] })
  const [savingKey, setSavingKey] = useState('')

  const effectiveBrandId = brandId || resolvedBrandId || location?.clinicBrandId
  const title = isLocation ? location?.title : brandTitle

  useEffect(() => {
    if (!open) return undefined
    let cancelled = false
    async function load() {
      setLoading(true)
      setError('')
      try {
        // Always load the brand workforce: it's the member source for brand scope, and the
        // candidate restriction (brand members) for location scope.
        let workforce = null
        if (brandSlug) {
          const resp = await getSupportClinicBrandBySlug(brandSlug)
          if (resp && isCommonResponseSuccessfulV2(resp)) {
            const brandData = getDataMapFromCommonResponse(resp)?.['clinic-brand']
            workforce = brandData?.workforce || null
            if (brandData?.id && !cancelled) setResolvedBrandId(brandData.id)
          }
        }
        if (cancelled) return
        setBrandWorkforce(workforce)
        if (isLocation) {
          setMembersByRole({
            screener: location?.screeners || [],
            therapist: location?.therapists || [],
            doctor: location?.doctors || [],
          })
        } else {
          setMembersByRole({
            screener: workforceListForRole(workforce, 'screener'),
            therapist: workforceListForRole(workforce, 'therapist'),
            doctor: workforceListForRole(workforce, 'doctor'),
          })
        }
      } catch (err) {
        if (!cancelled) setError('Unable to load clinic people.')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, brandSlug, scope, location?.id])

  const mentorsById = useMemo(() => {
    const map = new Map()
    mentors.forEach((mentor) => {
      if (mentor?.id) map.set(mentor.id, mentor)
    })
    return map
  }, [mentors])

  function candidatesForRole(role) {
    const current = membersByRole[role.key] || []
    if (isLocation) {
      // Restricted to brand members of this role not yet on the location.
      return workforceListForRole(brandWorkforce, role.key)
        .map((contact) => ({
          practitionerId: contact.uid,
          userId: contact.userId,
          label: contact.title || mentorName(mentorsById.get(contact.uid)),
        }))
        .filter(
          (option) =>
            option.practitionerId &&
            !current.some((m) => contactMatches(m, option.practitionerId, option.userId)),
        )
    }
    // Brand scope: any practitioner approved for the role, not already a member.
    return mentors
      .filter((mentor) => mentor?.teacherUnEditableFields?.servicesApproved?.[role.serviceKey])
      .filter((mentor) => !current.some((m) => contactMatches(m, mentor.id, mentor.userId)))
      .map((mentor) => ({
        practitionerId: mentor.id,
        userId: mentor.userId,
        label: mentorName(mentor),
      }))
  }

  async function handleAdd(role, option) {
    if (!option?.practitionerId) return
    const payload = { practitionerId: option.practitionerId, role: role.key }
    if (isLocation) payload.clinicLocationId = location?.id
    else payload.clinicBrandId = effectiveBrandId
    setSavingKey(`${role.key}:add`)
    try {
      const resp = isLocation
        ? await assignPractitionerToLocation(payload)
        : await assignPractitionerToBrand(payload)
      if (resp?.status === 200 && isCommonResponseSuccessfulV2(resp)) {
        const newContact = { uid: option.practitionerId, userId: option.userId, title: option.label }
        setMembersByRole((prev) => ({ ...prev, [role.key]: [...(prev[role.key] || []), newContact] }))
        toast.success(`Added ${option.label}`)
        onChanged?.()
      } else {
        toast.error(resp?.data?.message || 'Unable to add practitioner (check your clinic access).')
      }
    } catch (err) {
      toast.error('Unable to add practitioner.')
    } finally {
      setSavingKey('')
    }
  }

  async function handleRemove(role, contact) {
    const payload = { practitionerId: contact.uid, role: role.key }
    if (isLocation) payload.clinicLocationId = location?.id
    else payload.clinicBrandId = effectiveBrandId
    setSavingKey(`${role.key}:${contact.uid}`)
    try {
      const resp = isLocation
        ? await unassignPractitionerFromLocation(payload)
        : await unassignPractitionerFromBrand(payload)
      if (resp?.status === 200 && isCommonResponseSuccessfulV2(resp)) {
        setMembersByRole((prev) => ({
          ...prev,
          [role.key]: (prev[role.key] || []).filter(
            (m) => !contactMatches(m, contact.uid, contact.userId),
          ),
        }))
        toast.success('Removed')
        onChanged?.()
      } else {
        toast.error(resp?.data?.message || 'Unable to remove practitioner (check your clinic access).')
      }
    } catch (err) {
      toast.error('Unable to remove practitioner.')
    } finally {
      setSavingKey('')
    }
  }

  return (
    <Drawer anchor="right" open={Boolean(open)} onClose={onClose} PaperProps={{ sx: { width: { xs: '100%', sm: 460 } } }}>
      <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              People — {title || (isLocation ? 'Clinic location' : 'Clinic brand')}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {isLocation ? 'Clinic location' : 'Clinic brand'}
              {!canEdit && ' · read-only'}
            </Typography>
          </Box>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </Box>
      <Box sx={{ p: 2, overflowY: 'auto' }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {!canEdit && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Adding/removing practitioners is restricted to Clinic Success Admin and the attached
            Clinic Success Manager.
          </Alert>
        )}
        {isLocation && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Only practitioners already on this brand can be added to a location.
          </Alert>
        )}
        {loading ? (
          <Box display="flex" justifyContent="center" py={5}>
            <CircularProgress />
          </Box>
        ) : (
          <Stack spacing={2.5} divider={<Divider flexItem />}>
            {ROLES.map((role) => {
              const members = membersByRole[role.key] || []
              const candidates = canEdit ? candidatesForRole(role) : []
              return (
                <Box key={role.key}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                    {role.label} ({members.length})
                  </Typography>
                  <Stack spacing={0.75} sx={{ mb: canEdit ? 1.5 : 0 }}>
                    {members.length === 0 && (
                      <Typography variant="caption" color="text.secondary">
                        None yet.
                      </Typography>
                    )}
                    {members.map((contact) => {
                      const mentor = mentorsById.get(contact.uid)
                      const displayName = contact.title || mentorName(mentor)
                      const profilePicId = mentor?.profilePicId || contact.coverIcon
                      const slug = mentor?.slug || contact.slug
                      const practitionerId = contact.uid
                      return (
                        <Stack
                          key={contact.uid || contact.userId}
                          direction="row"
                          alignItems="center"
                          spacing={1}
                          justifyContent="space-between"
                          sx={{
                            px: 1,
                            py: 0.75,
                            borderRadius: 1,
                            bgcolor: 'rgba(0,0,0,0.03)',
                          }}
                        >
                          <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                            <Avatar
                              src={profilePicId ? getImageLink(profilePicId) : undefined}
                              sx={{ width: 34, height: 34, fontSize: 14 }}
                            >
                              {(displayName || '?').charAt(0).toUpperCase()}
                            </Avatar>
                            <Box sx={{ minWidth: 0 }}>
                              <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                                {displayName}
                              </Typography>
                              <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.25 }} flexWrap="wrap">
                                {practitionerId && (
                                  <Tooltip title={`Copy ID: ${practitionerId}`}>
                                    <Chip
                                      size="small"
                                      variant="outlined"
                                      label="ID"
                                      icon={<ContentCopyIcon sx={{ fontSize: 12 }} />}
                                      onClick={() => copyToClipboard(practitionerId, 'ID')}
                                      sx={{ height: 20, fontSize: '0.65rem', cursor: 'pointer' }}
                                    />
                                  </Tooltip>
                                )}
                                {slug && (
                                  <Tooltip title={`Copy slug: ${slug}`}>
                                    <Chip
                                      size="small"
                                      variant="outlined"
                                      label="Slug"
                                      icon={<ContentCopyIcon sx={{ fontSize: 12 }} />}
                                      onClick={() => copyToClipboard(slug, 'Slug')}
                                      sx={{ height: 20, fontSize: '0.65rem', cursor: 'pointer' }}
                                    />
                                  </Tooltip>
                                )}
                              </Stack>
                            </Box>
                          </Stack>
                          {canEdit &&
                            (savingKey === `${role.key}:${contact.uid}` ? (
                              <CircularProgress size={16} />
                            ) : (
                              <IconButton size="small" color="error" onClick={() => handleRemove(role, contact)}>
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            ))}
                        </Stack>
                      )
                    })}
                  </Stack>
                  {canEdit && (
                    <Autocomplete
                      size="small"
                      options={candidates}
                      getOptionLabel={(option) => option?.label || ''}
                      isOptionEqualToValue={(option, value) =>
                        option.practitionerId === value.practitionerId
                      }
                      value={null}
                      blurOnSelect
                      disabled={savingKey === `${role.key}:add`}
                      onChange={(_, option) => option && handleAdd(role, option)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={`Add ${role.label.slice(0, -1).toLowerCase()}`}
                          placeholder="Search practitioners"
                        />
                      )}
                      noOptionsText={
                        isLocation ? 'No eligible brand members' : 'No approved practitioners'
                      }
                    />
                  )}
                </Box>
              )
            })}
          </Stack>
        )}
      </Box>
    </Drawer>
  )
}
